import { useTranslation } from "react-i18next";
import { NOTIFICATION_ROUTE_MAP } from "@/constants/notificaiton";
import { useUpdateSingleNotificationAsReadMutation } from "@/services/notificationsApi";
import type { ISingleNotification } from "@/types/notifications.interface";
import {
  buildNotificationUrl,
  formatNotificaitonTime,
  notificationIconMap,
} from "@/utils/notifications.util";
import { Check, Info, Undo2 } from "lucide-react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";

interface IProps {
  notification: ISingleNotification;
  getAllNotificationRefetch: () => void;
  setOpen: (open: boolean) => void;
}

export const NotificationItem = ({
  notification,
  getAllNotificationRefetch,
  setOpen,
}: IProps) => {
  const { t } = useTranslation(["common", "nav", "company"]);
  const navigate = useNavigate();
  const {
    id,
    type,
    message,
    isRead,
    icon,
    platformSectionType,
    relatedEntityId,
    relatedEntityType,
    createdAt,
  } = notification;

  const Icon = notificationIconMap[icon.name] ?? Info;
  const iconBg = `rgba(${icon.color}, 0.12)`;
  const iconColor = `rgb(${icon.color})`;

  const [toggleRead, { isLoading }] =
    useUpdateSingleNotificationAsReadMutation();

  const handleToggleRead = async (e: React.MouseEvent) => {
    e.stopPropagation();
    const toastId = toast.loading("Processing...");
    await toggleRead(id)
      .unwrap()
      .then((res) => {
        getAllNotificationRefetch();
        toast.success(
          res.message ??
            (isRead
              ? t("common:inbox.marked_as_unread")
              : t("common:inbox.marked_as_read")),
          { id: toastId },
        );
      })
      .catch((e) => {
        toast.error(
          e?.data?.message ?? e.message ?? t("company:payrolls.action_failed"),
          {
            id: toastId,
          },
        );
      });
  };

  const handleClick = () => {
    setOpen(false);
    if (!isRead) toggleRead(id);
    // Unmapped section/entity pairs (e.g. a notification type the API ships
    // before the client knows it) mark as read without navigating.
    const route = NOTIFICATION_ROUTE_MAP[platformSectionType]?.[relatedEntityType];
    if (!route) return;
    navigate(buildNotificationUrl(route, relatedEntityId));
  };

  return (
    <div
      className={`group relative flex items-start gap-3 px-4 py-3.5 cursor-pointer transition-colors duration-150 ${
        !isRead ? "bg-primary/[0.06] hover:bg-primary/10" : "hover:bg-muted/40"
      }`}
      onClick={handleClick}
    >
      {/* Unread left accent bar */}
      {!isRead && (
        <span className="absolute start-0 top-2 bottom-2 w-[3px] rounded-e-full bg-primary" />
      )}

      {/* Notification icon */}
      <div
        className="flex shrink-0 h-9 w-9 items-center justify-center rounded-xl mt-0.5 shadow-sm"
        style={{ background: iconBg }}
      >
        <Icon size={15} style={{ color: iconColor }} />
      </div>

      {/* Text content */}
      <div className="flex-1 min-w-0 space-y-1.5">
        <p
          className={`text-[13px] leading-[1.4] line-clamp-2 pe-6 ${
            !isRead
              ? "font-semibold text-foreground"
              : "font-normal text-foreground/70"
          }`}
        >
          {message}
        </p>
        <div className="flex items-center justify-between gap-3">
          <span className="text-[11px] font-medium text-muted-foreground/80 truncate">
            {type}
          </span>
          <span className="shrink-0 text-[10px] text-muted-foreground/60 tabular-nums whitespace-nowrap">
            {formatNotificaitonTime(createdAt)}
          </span>
        </div>
      </div>

      {/* Hover action button — top right */}
      <button
        data-tooltip-id="hrm-tooltip"
        data-tooltip-content={
          isRead
            ? t("common:inbox.mark_as_unread")
            : t("common:inbox.mark_as_read")
        }
        disabled={isLoading}
        onClick={handleToggleRead}
        className="absolute top-3 end-3 flex h-6 w-6 items-center justify-center rounded-md border border-border/60 bg-background shadow-sm opacity-0 group-hover:opacity-100 transition-all duration-150 hover:border-primary/40 hover:bg-primary/5 disabled:opacity-40"
        aria-label={
          isRead
            ? t("common:inbox.mark_as_unread")
            : t("common:inbox.mark_as_read")
        }
      >
        {isRead ? (
          <Undo2 size={11} className="text-muted-foreground" />
        ) : (
          <Check size={11} className="text-primary" />
        )}
      </button>
    </div>
  );
};
